/* Re-scoping a month to a run of days and one department.
 *
 * The server sends a whole month already added up by day, shift and
 * department. Everything the toolbar can change — the day range, the
 * department — is a matter of summing those cells again, which is done
 * here so that no tab does its own arithmetic. */

import { SK } from '../../lib/api_attendance'
import type { AttDept, AttDesig, AttMonth, ByShift, ShiftKey } from '../../lib/api_attendance'

export interface ScopedDay {
  date: string; d: number; dow: string
  p: number; l: number; m: number; f: number; ot: number; h: number; u: number
  s: ByShift
}

export interface Scope {
  /** 'All', or a department on file. */
  sel: string
  isAll: boolean
  dFrom: number
  dTo: number
  fullRange: boolean
  /** The days in range, each cut down to the department in view. */
  sc: ScopedDay[]
  nDays: number
  /** Every department over the days in range, strongest first. */
  depts: AttDept[]
  /** The department in view over the whole period; null for All. */
  selBase: AttDept | null
  presentDays: number
  presentAvg: number
  totLate: number
  totMiss: number
  totFlagged: number
  totOt: number
  /** Present days in range with no shift code — they sit under O. */
  totNoShift: number
  activeRoll: number
  /** Sanctioned heads for what is in view; null when the sheet has none. */
  req: number | null
  reqShift: ByShift
  avgShift: ByShift
  gapAbs: number | null
  manPct: number | null
  attPct: number
  latePct: number
}

const r1 = (v: number) => Math.round(v * 10) / 10
export const zeroShift = (): ByShift => ({ A: 0, B: 0, C: 0, G: 0, O: 0 })

export function scopeMonth(
  D: AttMonth, dept: string | null, from: number | null, to: number | null,
): Scope {
  const sel = dept && D.depts.some((x) => x.name === dept) ? dept : 'All'
  const isAll = sel === 'All'

  const dayList = D.days.map((d) => d.d)
  const first = dayList[0], last = dayList[dayList.length - 1]
  const clamp = (v: number | null, fb: number) => (v !== null && dayList.includes(v) ? v : fb)
  let dFrom = clamp(from, first)
  let dTo = clamp(to, last)
  if (dFrom > dTo) [dFrom, dTo] = [dTo, dFrom]
  const fullRange = dFrom === first && dTo === last

  const rangeDays = D.days.filter((d) => d.d >= dFrom && d.d <= dTo)
  const sc: ScopedDay[] = rangeDays.map((d) => {
    const o = d.byDept[sel]
    const base = { date: d.date, d: d.d, dow: d.dow }
    if (isAll) {
      return { ...base, p: d.present, l: d.late, m: d.miss, f: d.flagged, ot: d.ot,
        h: d.avgHrs, u: d.noShift, s: d.byShift }
    }
    return o
      ? { ...base, p: o.p, l: o.l, m: o.m, f: o.f, ot: o.ot, h: o.h, u: o.u, s: o.s }
      : { ...base, p: 0, l: 0, m: 0, f: 0, ot: 0, h: 0, u: 0, s: zeroShift() }
  })
  const nDays = sc.length || 1

  // Short-hours flags and absences are not carried per day, so over a part
  // range those two stay as the whole period's.
  const depts = fullRange ? D.depts : D.depts.map((d) => {
    let p = 0, l = 0, m = 0, f = 0, ot = 0, u = 0
    const bs = zeroShift()
    rangeDays.forEach((day) => {
      const o = day.byDept[d.name]
      if (!o) return
      p += o.p; l += o.l; m += o.m; f += o.f; ot += o.ot; u += o.u
      SK.forEach((k) => { bs[k] += o.s[k] })
    })
    const per = zeroShift()
    SK.forEach((k) => { per[k] = r1(bs[k] / nDays) })
    return {
      ...d,
      avgPresent: r1(p / nDays),
      attPct: d.active ? r1((p / (d.active * nDays)) * 100) : 0,
      late: l, latePct: p ? r1((l / p) * 100) : 0,
      miss: m, flagged: f, ot: Math.round(ot), noShift: u, byShift: per,
    }
  }).sort((a, b) => b.avgPresent - a.avgPresent)

  const sum = (k: 'p' | 'l' | 'm' | 'f' | 'ot' | 'u') => sc.reduce((s, x) => s + x[k], 0)
  const presentDays = sum('p')
  const presentAvg = presentDays / nDays
  const totLate = sum('l')

  const selBase = isAll ? null : D.depts.find((x) => x.name === sel) ?? null
  const activeRoll = isAll ? D.activeRoll : selBase?.active ?? 0
  const req = isAll ? D.requiredTotal : selBase?.req ?? null
  // The sheet gives heads per shift for the plant and for each department.
  const reqShift = zeroShift()
  const fromSheet = isAll ? D.reqByShift : selBase?.reqShift
  if (fromSheet) SK.forEach((k) => { reqShift[k] = fromSheet[k] ?? 0 })
  const avgShift = zeroShift()
  SK.forEach((k) => { avgShift[k] = sc.reduce((s, x) => s + x.s[k], 0) / nDays })

  return {
    sel, isAll, dFrom, dTo, fullRange, sc, nDays, depts, selBase,
    presentDays, presentAvg, totLate,
    totMiss: sum('m'), totFlagged: sum('f'), totOt: sum('ot'), totNoShift: sum('u'),
    activeRoll, req, reqShift, avgShift,
    gapAbs: req ? req - presentAvg : null,
    manPct: req ? (presentAvg / req) * 100 : null,
    attPct: activeRoll ? (presentAvg / activeRoll) * 100 : 0,
    latePct: presentDays ? (totLate / presentDays) * 100 : 0,
  }
}

export interface DesigScope {
  /** The sheet's lines for what is in view — a designation is on the
   *  sheet or it is not counted — in the sheet's order. */
  rows: AttDesig[]
  /** Their departments, first appearance first. */
  depts: string[]
  /** Lines more than half a head under, and over, their requirement. */
  short: number
  over: number
  /** Heads on roll across those lines. */
  roll: number
}

/** The By-designation table and the Staffing KPIs read from the same
 *  lines; the day range does not touch them — the roll is for the month. */
export function scopeDesig(D: AttMonth, S: Scope): DesigScope {
  const rows = D.desig.filter((x) => x.req > 0 && (S.isAll || x.dept === S.sel))
  const gaps = rows.map((x) => x.roll - x.req)
  return {
    rows,
    depts: [...new Set(rows.map((x) => x.dept))],
    short: gaps.filter((g) => g < -0.5).length,
    over: gaps.filter((g) => g > 0.5).length,
    roll: rows.reduce((s, x) => s + x.roll, 0),
  }
}

/** Punch misses per 100 present days, so sections of any size compare. */
export const missRate = (d: AttDept, nDays: number) =>
  (d.avgPresent * nDays ? (d.miss / (d.avgPresent * nDays)) * 100 : 0)

export const otPerHead = (d: AttDept) => (d.active ? d.ot / d.active : 0)

export const shiftLabel = (k: ShiftKey) =>
  (k === 'O' ? '12-hr / other' : 'Shift ' + k + (k === 'G' ? ' (gen)' : ''))
